import moment from "moment";

import {
  DEFAULT_SCHEDULE_FORM,
  scheduleFormFromCurrent,
  scheduleHasInvoice,
} from "./patient-cost-share-schedule-form.js";

export const patientCostShare = {
  templateUrl: "admin/views/patient-cost-share.html",
  bindings: {
    patientId: "<",
  },
  //! @ngInject
  controller: function ($scope, $rootScope, DatabaseApi, toaster, $timeout, $uibModal, $sce) {
    const baseUrl = () =>
      `agencies/${$rootScope.agencyId}/agency_members/${$rootScope.agencyMemberId}`;

    // `<input type="date">` binds as a Date in local time; JSON-stringifying
    // a Date produces a UTC ISO datetime which shifts the day by the TZ
    // offset. Server LocalDate fields want plain "YYYY-MM-DD" in local time.
    const toLocalDate = (v) => {
      if (v === null || v === undefined || v === "") return v;
      if (typeof v === "string") return v.length > 10 ? v.slice(0, 10) : v;
      return moment(v).format("YYYY-MM-DD");
    };

    // Pull the most-specific error message out of the various shapes the
    // server / $http can return — but ONLY for errors we deliberately send
    // back (4xx). A 500 is an unhandled exception whose body is internal
    // (stack traces, provider auth errors, etc.) and must never reach the
    // user; for those we return null so the toast shows just its fallback.
    const serverErrorMessage = (err) => {
      if (err === null || err === undefined) return null;
      // AngularJS $http rejections expose `err.status`/`err.data` directly;
      // axios nests them under `err.response`. Support both.
      const status =
        typeof err.status === "number"
          ? err.status
          : err.response && err.response.status;
      if (typeof status === "number" && status >= 500) return null;
      const data = (err.response && err.response.data) || err.data;
      // Router2 typed errors: { errors: ["..."] }
      if (data && Array.isArray(data.errors) && data.errors.length > 0) {
        return data.errors.join("\n");
      }
      if (typeof data === "string") return data;
      if (data && typeof data.message === "string") return data.message;
      return null;
    };

    const errorToast = (fallback) => (err) => {
      console.log(err);
      const detail = serverErrorMessage(err);
      toaster.pop("error", fallback, detail || "");
    };

    // Cost share is FreedomCare-only.
    $scope.canView = () =>
      $rootScope.isFreedomCareAgency && $rootScope.isPermittedByKey("view_patient_cost_share");
    $scope.canEdit = () =>
      $rootScope.isFreedomCareAgency && $rootScope.isPermittedByKey("edit_patient_cost_share");

    $scope.isLoading = false;
    $scope.isGenerating = false;
    $scope.isCheckingEligibility = false;
    $scope.data = null;

    // As-of date the Generate button bills through. Defaults to today, which
    // is what the old "Generate through today" button always did. Must be a
    // Date, not a "YYYY-MM-DD" string: angular's input[type=date] $formatter
    // throws ngModel:datefmt on a non-Date model value. `toLocalDate` turns it
    // back into a plain local date string on submit.
    $scope.generateAsOfDate = moment().startOf("day").toDate();

    // Per-invoice expand/collapse state. Most-recent invoice opens by default.
    $scope.expandedInvoices = {};

    const currentInvoice = () =>
      $scope.data && $scope.data.invoices && $scope.data.invoices.length > 0
        ? $scope.data.invoices[0]
        : null;

    $scope.toggleInvoice = (invoiceId) => {
      $scope.expandedInvoices[invoiceId] = !$scope.expandedInvoices[invoiceId];
    };

    $scope.isInvoiceExpanded = (invoiceId) => !!$scope.expandedInvoices[invoiceId];

    $scope.scheduleForm = {
      visible: false,
      amountDollars: "",
      frequency: "monthly",
      startDate: "",
      paymentDate: "THURSDAY",
      errors: {},
    };

    $scope.endScheduleForm = {
      closeOn: "",
      errors: {},
    };

    $scope.paymentDayOptions = [
      "MONDAY",
      "TUESDAY",
      "WEDNESDAY",
      "THURSDAY",
      "FRIDAY",
      "SATURDAY",
      "SUNDAY",
    ];

    $scope.paymentDayLabel = (day) => {
      if (!day) return "";
      return day.charAt(0) + day.slice(1).toLowerCase();
    };
    $scope.paymentForm = {
      visible: false,
      amountDollars: "",
      receivedAt: "",
      notes: "",
      errors: {},
    };
    $scope.overrideForm = {
      visible: false,
      distributions: [],
      additionalRequests: [],
      capCents: 0,
      errors: {},
    };

    // Copy the patient's current (open) schedule into the edit form so the
    // modal always opens showing exactly what is saved. Returns true when a
    // current schedule existed and was applied, false otherwise.
    const fillScheduleFormFromCurrent = () => {
      const current = $scope.data && $scope.data.schedule && $scope.data.schedule.current;
      const values = scheduleFormFromCurrent(current);
      if (!values) return false;
      Object.assign($scope.scheduleForm, values);
      return true;
    };

    // Blank defaults for setting up a schedule for the first time.
    const resetScheduleFormToDefaults = () => {
      Object.assign($scope.scheduleForm, DEFAULT_SCHEDULE_FORM);
    };

    const loadData = async () => {
      if (!$scope.canView()) {
        return;
      }
      $scope.isLoading = true;
      try {
        const res = await DatabaseApi.get(
          `${baseUrl()}/patients/${$scope.$ctrl.patientId}/patient_cost_share`
        );
        $scope.$applyAsync(() => {
          $scope.data = res.data;
          // Open the most-recent invoice by default; preserve any user-toggled
          // state on subsequent reloads.
          if (res.data.invoices && res.data.invoices.length > 0) {
            const mostRecentId = res.data.invoices[0].id;
            if (!(mostRecentId in $scope.expandedInvoices)) {
              $scope.expandedInvoices[mostRecentId] = true;
            }
          }
        });
      } catch (err) {
        errorToast("Failed to load patient cost share")(err);
      } finally {
        $timeout(() => ($scope.isLoading = false));
      }
    };

    // The server pushes this once n8n finishes a generate/finalize or a link
    // send for this patient. Refresh in place if we're still viewing them.
    // $scope.$on (not $rootScope.$on) auto-unsubscribes when the controller
    // is destroyed, so it only fires while the view is active.
    $scope.$on("PatientCostShareUpdated", (_event, data) => {
      if (data && Number(data.patientId) === Number($scope.$ctrl.patientId)) {
        loadData();
      }
    });

    // Patient-level (not schedule-level) since V1610: the delivery method
    // decides which contact detail must be on file for anything — pay link or
    // charge receipt — to reach the patient.
    $scope.linkDeliveryMethod = () =>
      ($scope.data && $scope.data.config ? $scope.data.config.linkDeliveryMethod : null) ||
      "email";

    $scope.patientMissingContact = () => {
      if (!$scope.data) return false;
      return $scope.linkDeliveryMethod() === "sms"
        ? !$scope.data.patientHasMobilePhone
        : !$scope.data.patientHasEmail;
    };

    $scope.missingContactText = () =>
      $scope.linkDeliveryMethod() === "sms"
        ? "This patient has no mobile phone on file — payment links cannot be sent by SMS. Add a mobile number to the patient's profile."
        : "This patient has no email on file — payment links cannot be sent. Add an email to the patient's profile.";

    // Human label for a delivery method (used in missing-contact messages).
    $scope.deliveryMethodLabel = (method) => (method === "sms" ? "SMS" : "email");

    // True when the current schedule has already produced an invoice. In that
    // case a material change (amount / frequency / payment day) supersedes and
    // only takes effect on next month's invoice; a delivery-method-only change
    // still applies immediately. Mirrors the server's supersede condition.
    $scope.currentScheduleHasInvoice = () =>
      scheduleHasInvoice(
        $scope.data && $scope.data.schedule && $scope.data.schedule.current,
        $scope.data && $scope.data.invoices
      );

    $scope.openScheduleModal = () => {
      if (!$scope.canEdit()) return;
      // Always start from the saved schedule: when updating, prefill every
      // field from the current schedule; when setting one up for the first
      // time, fall back to blank defaults so a prior aborted edit doesn't
      // linger in the form.
      if (!fillScheduleFormFromCurrent()) {
        resetScheduleFormToDefaults();
      }
      $scope.scheduleForm.errors = {};
      $scope.scheduleModal = $uibModal.open({
        templateUrl: "admin/views/patient-cost-share-schedule-modal.html",
        scope: $scope,
        size: "md",
      });
    };

    $scope.submitSchedule = async () => {
      if (!$scope.canEdit()) return;
      // Updating an existing open schedule edits it in place (start date is
      // fixed); only creating a brand-new one takes a start date.
      const current = $scope.data && $scope.data.schedule && $scope.data.schedule.current;
      const isUpdate = !!current;
      const errors = {};
      const amountCents = Math.round(parseFloat($scope.scheduleForm.amountDollars || "0") * 100);
      if (!Number.isFinite(amountCents) || amountCents <= 0) {
        errors.amount = "Amount must be greater than zero";
      }
      if (!isUpdate && !$scope.scheduleForm.startDate) {
        errors.startDate = "Start date is required";
      }
      if (!$scope.scheduleForm.frequency) {
        errors.frequency = "Frequency is required";
      }
      if (!$scope.scheduleForm.paymentDate) {
        errors.paymentDate = "Payment day is required";
      }
      $scope.scheduleForm.errors = errors;
      if (Object.keys(errors).length > 0) {
        toaster.pop("error", "Please fix the highlighted fields");
        return;
      }
      const base = `${baseUrl()}/patients/${$scope.$ctrl.patientId}/patient_cost_share/schedule`;
      try {
        if (isUpdate) {
          // In-place update: everything except the start date. No end-then-recreate.
          await DatabaseApi.post(`${base}/${current.id}/update`, {
            amountCents,
            frequency: $scope.scheduleForm.frequency,
            paymentDate: $scope.scheduleForm.paymentDate,
          });
        } else {
          await DatabaseApi.post(base, {
            amountCents,
            frequency: $scope.scheduleForm.frequency,
            startDate: toLocalDate($scope.scheduleForm.startDate),
            paymentDate: $scope.scheduleForm.paymentDate,
          });
        }
        toaster.pop("success", "Schedule saved");
        if ($scope.scheduleModal) $scope.scheduleModal.close();
        await loadData();
      } catch (err) {
        errorToast("Failed to save schedule")(err);
      }
    };

    $scope.openEndScheduleModal = () => {
      if (!$scope.canEdit() || !$scope.data || !$scope.data.schedule.current) return;
      $scope.endScheduleForm.errors = {};
      $scope.endScheduleForm.closeOn = moment().format("YYYY-MM-DD");
      $scope.endScheduleModal = $uibModal.open({
        templateUrl: "admin/views/patient-cost-share-end-schedule-modal.html",
        scope: $scope,
        size: "md",
      });
    };

    $scope.submitEndSchedule = async () => {
      if (!$scope.canEdit()) return;
      const current = $scope.data && $scope.data.schedule.current;
      if (!current) return;
      const errors = {};
      const closeOn = toLocalDate($scope.endScheduleForm.closeOn);
      if (!closeOn) {
        errors.closeOn = "Close-on date is required";
      } else if (closeOn < current.startDate) {
        errors.closeOn = `Close-on must be on or after the schedule start (${current.startDate})`;
      }
      $scope.endScheduleForm.errors = errors;
      if (Object.keys(errors).length > 0) {
        toaster.pop("error", "Please fix the highlighted fields");
        return;
      }
      try {
        await DatabaseApi.post(
          `${baseUrl()}/patients/${$scope.$ctrl.patientId}/patient_cost_share/schedule/${current.id}/close?closeOn=${encodeURIComponent(closeOn)}`,
          {}
        );
        toaster.pop("success", "Schedule ended");
        if ($scope.endScheduleModal) $scope.endScheduleModal.close();
        await loadData();
      } catch (err) {
        errorToast("Failed to end schedule")(err);
      }
    };

    $scope.generate = async () => {
      if (!$scope.canEdit() || $scope.isGenerating) return;
      const asOfDate = toLocalDate($scope.generateAsOfDate);
      if (!asOfDate) {
        toaster.pop("error", "Pick an as-of date to generate through");
        return;
      }
      $scope.$applyAsync(() => ($scope.isGenerating = true));
      try {
        // Async: this only kicks off the n8n generate→finalize flow. The page
        // refreshes when n8n reports back via the PatientCostShareUpdated
        // socket event (handled below).
        await DatabaseApi.post(`${baseUrl()}/patient_cost_share/generate_invoices`, {
          asOfDate,
          patientId: Number($scope.$ctrl.patientId),
        });
        toaster.pop(
          "success",
          "Generating invoices…",
          "This runs in the background; the page refreshes when it's done."
        );
      } catch (err) {
        errorToast("Failed to start generation")(err);
      } finally {
        $timeout(() => ($scope.isGenerating = false));
      }
    };

    $scope.runEligibility = async () => {
      if (!$scope.canEdit() || $scope.isCheckingEligibility) return;
      $scope.$applyAsync(() => ($scope.isCheckingEligibility = true));
      try {
        // Async: kicks off the n8n real-time STEDI eligibility check for this
        // patient (sets the schedule from the result). The view refreshes when
        // n8n reports back via the PatientCostShareUpdated socket event.
        await DatabaseApi.post(
          `${baseUrl()}/patients/${$scope.$ctrl.patientId}/patient_cost_share/run_eligibility`,
          {}
        );
        toaster.pop(
          "success",
          "Checking eligibility…",
          "The schedule updates when the check completes (usually seconds)."
        );
      } catch (err) {
        errorToast("Failed to start eligibility check")(err);
      } finally {
        $timeout(() => ($scope.isCheckingEligibility = false));
      }
    };

    $scope.openOverrideModal = () => {
      if (!$scope.canEdit()) return;
      const invoice = currentInvoice();
      if (!invoice) return;
      $scope.overrideForm.errors = {};
      $scope.overrideForm.distributions = invoice.paymentRequests
        .filter((r) => r.status === "pending" || r.status === "awaiting_payment")
        .map((r) => ({
          paymentRequestId: r.id,
          dueDate: r.dueDate,
          baseAmountCents: r.baseAmountCents,
          overrideDollars: (r.overrideAmountCents / 100).toFixed(2),
        }));
      $scope.overrideForm.additionalRequests = ($scope.data.currentMonthExtraWeeklyDates || []).map(
        (dueDate) => ({
          dueDate,
          enabled: false,
          overrideDollars: "0.00",
        })
      );
      $scope.overrideForm.capCents = invoice.pastBalanceCents || 0;
      $scope.overrideModal = $uibModal.open({
        templateUrl: "admin/views/patient-cost-share-override-modal.html",
        scope: $scope,
        size: "lg",
      });
    };

    $scope.overrideRowNewAmountCents = (d) => {
      const override = Math.round((parseFloat(d.overrideDollars) || 0) * 100);
      return d.baseAmountCents + override;
    };

    $scope.overrideExtraNewAmountDollars = (r) => {
      const v = parseFloat(r.overrideDollars);
      return Number.isFinite(v) ? v.toFixed(2) : "0.00";
    };

    $scope.overrideRowInvalid = (d) => {
      const override = parseFloat(d.overrideDollars);
      if (!Number.isFinite(override)) return true;
      const overrideCents = Math.round(override * 100);
      return overrideCents < -d.baseAmountCents;
    };

    $scope.overrideExtraInvalid = (r) => {
      if (!r.enabled) return false;
      const v = parseFloat(r.overrideDollars);
      return !Number.isFinite(v) || v <= 0;
    };

    $scope.overrideTotalDollars = () => {
      const dists = $scope.overrideForm.distributions.reduce(
        (acc, d) => acc + (parseFloat(d.overrideDollars) || 0),
        0
      );
      const extras = $scope.overrideForm.additionalRequests
        .filter((r) => r.enabled)
        .reduce((acc, r) => acc + (parseFloat(r.overrideDollars) || 0), 0);
      return (dists + extras).toFixed(2);
    };

    $scope.overrideExceedsCap = () => {
      const totalCents = Math.round(parseFloat($scope.overrideTotalDollars()) * 100);
      return totalCents > $scope.overrideForm.capCents;
    };

    $scope.submitOverride = async () => {
      const invoice = currentInvoice();
      if (!$scope.canEdit() || !invoice) return;
      const errors = {};
      $scope.overrideForm.distributions.forEach((d) => {
        if ($scope.overrideRowInvalid(d)) {
          errors[`dist_${d.paymentRequestId}`] = true;
        }
      });
      $scope.overrideForm.additionalRequests.forEach((r, i) => {
        if ($scope.overrideExtraInvalid(r)) {
          errors[`extra_${i}`] = true;
        }
      });
      if ($scope.overrideExceedsCap()) {
        errors.cap = `Total override cannot exceed $${(
          $scope.overrideForm.capCents / 100
        ).toFixed(2)} (invoice past balance).`;
      }
      $scope.overrideForm.errors = errors;
      if (Object.keys(errors).length > 0) {
        toaster.pop("error", "Please fix the highlighted rows");
        return;
      }
      try {
        await DatabaseApi.post(
          `${baseUrl()}/patient_cost_share/invoices/${invoice.id}/override`,
          {
            distributions: $scope.overrideForm.distributions.map((d) => ({
              paymentRequestId: d.paymentRequestId,
              overrideAmountCents: Math.max(
                -d.baseAmountCents,
                Math.round(parseFloat(d.overrideDollars || "0") * 100)
              ),
            })),
            additionalRequests: $scope.overrideForm.additionalRequests
              .filter((r) => r.enabled)
              .map((r) => ({
                dueDate: r.dueDate,
                overrideAmountCents: Math.max(
                  0,
                  Math.round(parseFloat(r.overrideDollars || "0") * 100)
                ),
              })),
          }
        );
        toaster.pop("success", "Override applied");
        if ($scope.overrideModal) $scope.overrideModal.close();
        await loadData();
      } catch (err) {
        errorToast("Failed to apply override")(err);
      }
    };

    $scope.openPaymentModal = () => {
      if (!$scope.canEdit()) return;
      const invoice = currentInvoice();
      $scope.paymentForm.errors = {};
      $scope.paymentForm.amountDollars = invoice ? (invoice.balanceCents / 100).toFixed(2) : "";
      $scope.paymentForm.receivedAt = new Date().toISOString().slice(0, 16);
      $scope.paymentForm.notes = "";
      $scope.paymentModal = $uibModal.open({
        templateUrl: "admin/views/patient-cost-share-payment-modal.html",
        scope: $scope,
        size: "md",
      });
    };

    $scope.submitPayment = async () => {
      const invoice = currentInvoice();
      if (!$scope.canEdit() || !invoice) return;
      const errors = {};
      const amountCents = Math.round(parseFloat($scope.paymentForm.amountDollars || "0") * 100);
      if (!Number.isFinite(amountCents) || amountCents <= 0) {
        errors.amount = "Amount must be greater than zero";
      } else if (amountCents > invoice.balanceCents) {
        errors.amount = `Amount cannot exceed the invoice balance ($${(
          invoice.balanceCents / 100
        ).toFixed(2)})`;
      }
      if (!$scope.paymentForm.receivedAt) {
        errors.receivedAt = "Received-at is required";
      }
      $scope.paymentForm.errors = errors;
      if (Object.keys(errors).length > 0) {
        toaster.pop("error", "Please fix the highlighted fields");
        return;
      }
      try {
        await DatabaseApi.post(
          `${baseUrl()}/patients/${$scope.$ctrl.patientId}/patient_cost_share/payments`,
          {
            invoiceId: invoice.id,
            amountCents,
            receivedAt: new Date($scope.paymentForm.receivedAt).toISOString(),
            notes: $scope.paymentForm.notes.trim() === "" ? null : $scope.paymentForm.notes.trim(),
          }
        );
        toaster.pop("success", "Payment recorded");
        if ($scope.paymentModal) $scope.paymentModal.close();
        $scope.paymentForm.notes = "";
        await loadData();
      } catch (err) {
        errorToast("Failed to record payment")(err);
      }
    };

    $scope.voidInvoice = async (invoice) => {
      if (!$scope.canEdit()) return;
      if (!confirm(`Void invoice for ${invoice.periodStart} – ${invoice.periodEnd}?`)) return;
      try {
        await DatabaseApi.post(`${baseUrl()}/patient_cost_share/invoices/${invoice.id}/void`, {});
        toaster.pop("success", "Invoice voided");
        await loadData();
      } catch (err) {
        errorToast("Failed to void invoice")(err);
      }
    };

    $scope.sendingFor = {};

    $scope.sendPayLink = async (request) => {
      if (!$scope.canEdit() || $scope.sendingFor[request.id]) return;
      $scope.$applyAsync(() => ($scope.sendingFor[request.id] = true));
      try {
        // Async: kicks off the n8n send flow. The row's sent state updates
        // when n8n reports back via the PatientCostShareUpdated socket event.
        await DatabaseApi.post(
          `${baseUrl()}/patient_cost_share/payment_requests/${request.id}/send_link`,
          {}
        );
        toaster.pop(
          "success",
          "Sending pay link…",
          "The row updates once it's sent."
        );
      } catch (err) {
        errorToast("Failed to start sending the pay link")(err);
      } finally {
        $timeout(() => ($scope.sendingFor[request.id] = false));
      }
    };

    $scope.sendLinkLabel = (request) => {
      if ($scope.sendingFor[request.id]) return "Sending…";
      if (request.lastEmailSentAt) return "Resend link";
      return "Send link";
    };

    $scope.copiedFor = {};
    $scope.copyPayLink = async (request) => {
      if (!request.shareUrl) return;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(request.shareUrl);
        } else {
          const textarea = document.createElement("textarea");
          textarea.value = request.shareUrl;
          textarea.style.position = "fixed";
          textarea.style.opacity = "0";
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand("copy");
          document.body.removeChild(textarea);
        }
        $scope.$applyAsync(() => ($scope.copiedFor[request.id] = true));
        $timeout(() => ($scope.copiedFor[request.id] = false), 1500);
      } catch (err) {
        errorToast("Failed to copy link")(err);
      }
    };

    $scope.copyLinkLabel = (request) =>
      $scope.copiedFor[request.id] ? "Copied!" : "Copy link";

    // Why the Send/Copy buttons are disabled, in priority order. Returns ""
    // when the action is available (tooltip stays hidden in that case). The
    // transient sending/copied states are intentionally omitted — the button
    // label already reflects those.
    // Send/Copy stay available on auto-charge rows on purpose: if a charge
    // fails, or is going to, an operator needs to be able to fall back to a pay
    // link without first reconfiguring the patient. The buttons only exist while
    // the request is `awaiting_payment` (see the template), so a paid or charged
    // request offers nothing to send.
    $scope.sendLinkDisabledReason = (request, invoice) => {
      if (!invoice.stripeInvoiceId) return "Invoices not set in payment platform";
      if (invoice.isBackfilled)
        return "This is a backfilled invoice and cannot be paid through the platform";
      // intendedSendTo is computed server-side from THIS request's own schedule
      // delivery method; null means the patient has no contact for that method.
      if (!request.intendedSendTo)
        return request.linkDeliveryMethod === "sms"
          ? "This patient has no mobile phone on file — the link cannot be sent by SMS. Add a mobile number to the patient's profile."
          : "This patient has no email on file — the link cannot be sent. Add an email to the patient's profile.";
      return "";
    };

    $scope.copyLinkDisabledReason = (request, invoice) => {
      if (!invoice.stripeInvoiceId) return "Invoices not set in payment platform";
      if (invoice.isBackfilled)
        return "This is a backfilled invoice and cannot be paid through the platform";
      if (!request.shareUrl) return "No payment link has been generated yet";
      return "";
    };

    // ---- Charge vs link -----------------------------------------------------

    // Whether the PATIENT is configured to charge. Distinct from
    // isChargeRequest: this is the intent, that is what will actually happen
    // (which also needs a saved card). Drives the summary and the no-card warning.
    $scope.isChargeSchedule = () =>
      !!($scope.data && $scope.data.config && $scope.data.config.collectionMethod === "charge");

    // "Visa ending in 4242", or "" when no card is saved.
    $scope.savedCardLabel = () => {
      const pm = $scope.data && $scope.data.savedPaymentMethod;
      if (!pm || !pm.last4) return "";
      const brand = pm.brand
        ? pm.brand.charAt(0).toUpperCase() + pm.brand.slice(1)
        : "Card";
      return `${brand} ending in ${pm.last4}`;
    };

    // What will actually happen to this request (server-resolved: charge mode
    // only counts when a saved card exists). For a paid request, prefer what
    // actually happened over what was intended.
    $scope.isChargeRequest = (request) => {
      if (!request) return false;
      if (request.collectedVia) return request.collectedVia === "charge";
      return request.effectiveCollectionMethod === "charge";
    };

    // Whether an invoice's money moved (or will move) through the TEST Stripe
    // account. Read off the invoice, not off the toggles: an invoice keeps the
    // account it was generated against even after the patient is switched.
    $scope.isTestInvoice = (invoice) => !!invoice && invoice.stripeMode !== "live";

    // Everything that names a collection method says so out loud when it is
    // only test money — "Pay link (test)" — so nobody reads a sandbox charge as
    // a real one.
    const withTestSuffix = (label, isTest) => (isTest ? `${label} (test)` : label);

    $scope.collectionLabel = (request, invoice) => {
      const isTest = $scope.isTestInvoice(invoice);
      if (request.collectedVia)
        return withTestSuffix(
          request.collectedVia === "charge" ? "Charged" : "Paid by link",
          isTest
        );
      // request.linkDeliveryMethod is per-request and server-resolved, so a row
      // says how ITS next message would go out rather than echoing whatever the
      // patient's settings happen to be right now.
      const base = $scope.isChargeRequest(request)
        ? "Auto-charge"
        : request.linkDeliveryMethod === "sms"
        ? "SMS"
        : "Pay link";
      return withTestSuffix(base, isTest);
    };

    $scope.collectionClass = (request) =>
      $scope.isChargeRequest(request) ? "label-primary" : "label-default";

    // ---- Test / production Stripe account ----------------------------------

    // Two switches decide where a patient's money goes, and both have to be on
    // for it to be real: the agency has to have turned production cost share
    // on at all, and this patient has to be in production mode. Everything the
    // UI says about "test" is derived from these, never guessed.
    $scope.agencyProductionEnabled = () =>
      !!($scope.data && $scope.data.agencyProductionEnabled);

    $scope.patientProductionMode = () =>
      !!($scope.data && $scope.data.config && $scope.data.config.productionMode);

    /** What the NEXT invoice for this patient would be billed against. */
    $scope.effectiveStripeMode = () =>
      ($scope.data && $scope.data.effectiveStripeMode) || "test";

    $scope.isProductionMode = () => $scope.effectiveStripeMode() === "live";

    // The toggle shows the PATIENT's own setting — that is what clicking it
    // changes — while the tooltip explains when that setting is being
    // overruled by the agency switch being off.
    $scope.modeToggleLabel = () => ($scope.patientProductionMode() ? "Production" : "Test");

    // Explicit colours, not theme classes: this skin has no green button —
    // btn-success renders rgb(28,132,198) and btn-primary rgb(76,145,247), i.e.
    // two blues, which would make the whole point of the toggle invisible.
    // Green only when the patient is ACTUALLY live, so the colour tracks where
    // the money really goes rather than the patient's setting on its own.
    $scope.modeToggleStyle = () =>
      $scope.patientProductionMode() && $scope.agencyProductionEnabled()
        ? { "background-color": "#18a957", "border-color": "#158f4a", color: "#fff" }
        : { "background-color": "#4c91f7", "border-color": "#3f7fd8", color: "#fff" };

    $scope.modeToggleTooltip = () => {
      if (!$scope.patientProductionMode()) {
        return "This patient is held on the test Stripe account. Click to move them to production.";
      }
      if (!$scope.agencyProductionEnabled()) {
        return (
          "This patient is set to production, but production cost share is off for the agency, " +
          "so they are still billed against the test Stripe account. Turn it on in Settings → " +
          "Agency Settings."
        );
      }
      return "This patient is billed for real. Click to move them back to the test Stripe account.";
    };

    $scope.isSavingMode = false;

    // Existing invoices keep the account they were generated against, so this
    // only ever changes what happens NEXT — worth saying out loud before
    // someone clicks it expecting open invoices to move.
    $scope.toggleProductionMode = async () => {
      if (!$scope.canEdit() || !$scope.data || $scope.isSavingMode) return;
      const next = !$scope.patientProductionMode();
      const confirmText = next
        ? "Move this patient to the production Stripe account? New invoices and pay links will charge real money" +
          ($scope.agencyProductionEnabled()
            ? "."
            : " once production cost share is turned on for the agency.")
        : "Move this patient back to the test Stripe account? New invoices and pay links will be test-only. Invoices already generated keep the account they were created against.";
      if (!window.confirm(confirmText)) return;

      $scope.$applyAsync(() => ($scope.isSavingMode = true));
      try {
        await DatabaseApi.post(
          `${baseUrl()}/patients/${$scope.$ctrl.patientId}/patient_cost_share/mode`,
          { productionMode: next }
        );
        toaster.pop(
          "success",
          next ? "Patient moved to production" : "Patient moved to test"
        );
        await loadData();
      } catch (err) {
        errorToast("Failed to change the patient's Stripe mode")(err);
      } finally {
        $timeout(() => ($scope.isSavingMode = false));
      }
    };

    // ---- Patient-level collection settings ---------------------------------

    $scope.configForm = {
      collectionMethod: "link",
      linkDeliveryMethod: "email",
      errors: {},
    };

    $scope.openConfigModal = () => {
      if (!$scope.canEdit() || !$scope.data) return;
      const cfg = $scope.data.config || {};
      $scope.configForm = {
        collectionMethod: cfg.collectionMethod || "link",
        linkDeliveryMethod: cfg.linkDeliveryMethod || "email",
        errors: {},
      };
      $scope.configModal = $uibModal.open({
        templateUrl: "admin/views/patient-cost-share-config-modal.html",
        scope: $scope,
        size: "md",
      });
    };

    $scope.submitConfig = async () => {
      if (!$scope.canEdit()) return;
      const errors = {};
      if (!$scope.configForm.collectionMethod) {
        errors.collectionMethod = "Collection method is required";
      }
      if (!$scope.configForm.linkDeliveryMethod) {
        errors.linkDeliveryMethod = "Delivery method is required";
      }
      $scope.configForm.errors = errors;
      if (Object.keys(errors).length > 0) {
        toaster.pop("error", "Please fix the highlighted fields");
        return;
      }
      try {
        await DatabaseApi.post(
          `${baseUrl()}/patients/${$scope.$ctrl.patientId}/patient_cost_share/config`,
          {
            collectionMethod: $scope.configForm.collectionMethod,
            linkDeliveryMethod: $scope.configForm.linkDeliveryMethod,
          }
        );
        toaster.pop("success", "Collection settings saved");
        if ($scope.configModal) $scope.configModal.close();
        await loadData();
      } catch (err) {
        errorToast("Failed to save collection settings")(err);
      }
    };

    // ---- Notification history --------------------------------------------

    const NOTIFICATION_CAUSE_LABELS = {
        scheduled_worker: "daily run",
        scheduled_charge: "daily run",
        manual_send: "sent by",
        manual_charge: "charged by",
        payment_received: "payment received",
    };

    const NOTIFICATION_KIND_LABELS = {
        invoice: "Invoice",
        pay_link: "Pay link",
        payment_receipt: "Receipt",
        charge_receipt: "Charge receipt",
    };

    $scope.notificationCount = (request) =>
      request && Array.isArray(request.notifications) ? request.notifications.length : 0;

    // One line per delivered message, newest first — what it was, how it went,
    // where to, when, and what caused it.
    $scope.notificationHistory = (request) => {
      if ($scope.notificationCount(request) === 0) return "";
      return request.notifications
        .map((n) => {
          const what = NOTIFICATION_KIND_LABELS[n.kind] || n.kind;
          const how = n.deliveryMethod === "sms" ? "SMS" : "email";
          const cause = NOTIFICATION_CAUSE_LABELS[n.cause] || n.cause;
          const who = n.causedByName ? `${cause} ${n.causedByName}` : cause;
          return `${$scope.formatSentAt(n.sentAt)} · ${what} by ${how} to ${n.sentTo} (${who})`;
        })
        .join("\n");
    };

    // ---- Delivered message viewer ----------------------------------------

    const latestOfKind = (notifications, kinds) =>
      (notifications || []).find((n) => kinds.includes(n.kind)) || null;

    // Newest receipt / invoice email, for the clickable "sent" indicators.
    $scope.latestReceipt = (request) =>
      latestOfKind(request && request.notifications, ["payment_receipt", "charge_receipt"]);
    $scope.latestInvoiceEmail = (invoice) =>
      latestOfKind(invoice && invoice.notifications, ["invoice"]);

    $scope.notificationLabel = (n) => {
      const what = NOTIFICATION_KIND_LABELS[n.kind] || n.kind;
      const how = n.deliveryMethod === "sms" ? "SMS" : "email";
      return `${what} by ${how}`;
    };

    $scope.notificationCauseLabel = (n) => {
      const cause = NOTIFICATION_CAUSE_LABELS[n.cause] || n.cause;
      return n.causedByName ? `${cause} ${n.causedByName}` : cause;
    };

    // Full delivery history of one payment request (or invoice), each entry
    // openable to show the message itself.
    $scope.openNotificationHistory = (title, notifications) => {
      $scope.historyModalData = { title, notifications: notifications || [] };
      $uibModal.open({
        templateUrl: "admin/views/patient-cost-share-history-modal.html",
        scope: $scope,
        size: "lg",
      });
    };

    // Fetches and shows what a notification actually delivered. Email HTML is
    // rendered in a sandboxed iframe (no scripts, no access to this page).
    $scope.viewNotification = async (notification) => {
      if (!notification) return;
      $scope.messageModalData = { notification, isLoading: true, content: null };
      $uibModal.open({
        templateUrl: "admin/views/patient-cost-share-message-modal.html",
        scope: $scope,
        size: "lg",
      });
      try {
        const res = await DatabaseApi.get(
          `${baseUrl()}/patient_cost_share/notifications/${notification.id}`
        );
        const content = res.data;
        $scope.$applyAsync(() => {
          $scope.messageModalData.content = content;
          $scope.messageModalData.isEmail = content.deliveryMethod === "email";
          $scope.messageModalData.trustedHtml =
            content.deliveryMethod === "email" && content.body
              ? $sce.trustAsHtml(content.body)
              : null;
          $scope.messageModalData.isLoading = false;
        });
      } catch (err) {
        $scope.$applyAsync(() => ($scope.messageModalData.isLoading = false));
        errorToast("Failed to load the message")(err);
      }
    };

    $scope.isRemovingCard = false;

    $scope.removeSavedCard = async () => {
      if (!$scope.canEdit() || $scope.isRemovingCard) return;
      const card = $scope.savedCardLabel();
      if (!card) return;
      // Detaching is not reversible — the patient has to re-enter the card — so
      // spell out both consequences before doing it.
      if (
        !window.confirm(
          `Remove ${card}?\n\nThe card is detached from the patient and any unpaid ` +
            `pay links are reset, so they'll be asked for card details again. ` +
            `Auto-charging falls back to pay links until a new card is saved.`
        )
      ) {
        return;
      }
      $scope.$applyAsync(() => ($scope.isRemovingCard = true));
      try {
        const res = await DatabaseApi.post(
          `${baseUrl()}/patients/${$scope.$ctrl.patientId}/patient_cost_share/remove_payment_method`,
          {}
        );
        const d = (res && res.data) || {};
        const parts = [];
        if (d.linksReset) parts.push(`${d.linksReset} pay link(s) reset`);
        if (d.linksSkipped)
          parts.push(`${d.linksSkipped} left alone (payment in progress)`);
        toaster.pop("success", "Payment method removed", parts.join(" · "));
        if ($scope.configModal) $scope.configModal.close();
        await loadData();
      } catch (err) {
        errorToast("Failed to remove the payment method")(err);
      } finally {
        $timeout(() => ($scope.isRemovingCard = false));
      }
    };

    $scope.chargingFor = {};

    $scope.chargeDisabledReason = (request, invoice) => {
      if (invoice.voidedAt) return "This invoice is voided";
      if (invoice.isBackfilled)
        return "This is a backfilled invoice and cannot be charged through the platform";
      if (!$scope.savedCardLabel())
        return "This patient has no saved payment method to charge";
      return "";
    };

    $scope.chargeNow = async (request) => {
      if (!$scope.canEdit() || $scope.chargingFor[request.id]) return;
      const card = $scope.savedCardLabel() || "the saved payment method";
      const amount = `$${$scope.dollarsFromCents(request.amountCents)}`;
      // Taking money is not undoable from here, so confirm with the actual
      // amount and card before doing it.
      if (!window.confirm(`Charge ${amount} to ${card} now?`)) return;
      $scope.$applyAsync(() => ($scope.chargingFor[request.id] = true));
      try {
        // Synchronous, unlike sendPayLink: the response tells us whether the
        // card was actually charged, so reload to pick up the new paid state.
        await DatabaseApi.post(
          `${baseUrl()}/patient_cost_share/payment_requests/${request.id}/charge`,
          {}
        );
        toaster.pop("success", `Charged ${amount}`, "A receipt is on its way to the patient.");
        await loadData();
      } catch (err) {
        errorToast("Charge failed")(err);
      } finally {
        $timeout(() => ($scope.chargingFor[request.id] = false));
      }
    };

    // Green only for an answer (cost share or none) checked this month. An
    // error says nothing about the amount, so it stays red.
    $scope.stediCheckIsCurrent = () => {
      const check = $scope.data && $scope.data.lastStediCheck;
      return (
        !!check && check.outcome !== "error" && moment(check.checkedAt).isSame(moment(), "month")
      );
    };

    $scope.stediCheckLabel = () => {
      const check = $scope.data && $scope.data.lastStediCheck;
      if (!check) return "No eligibility check yet";
      const when = `Last eligibility check: ${$scope.formatSentAt(check.checkedAt)}`;
      if (check.outcome === "cost_share") {
        return `${when} · $${$scope.dollarsFromCents(check.amountCents)} cost share`;
      }
      if (check.outcome === "no_cost_share") return `${when} · no cost share`;
      return `${when} · error: ${check.errorMessage || "no answer"}`;
    };

    $scope.formatSentAt = (instant) => {
      if (!instant) return "";
      return moment(instant).format("MMM D, YYYY h:mm A");
    };

    $scope.formatDate = (date) => {
      if (!date) return "";
      return moment(date).format("MM/DD/YYYY");
    };

    $scope.dollarsFromCents = (cents) => ((cents || 0) / 100).toFixed(2);

    $scope.statusLabel = (status) => {
      switch (status) {
        case "paid":
          return "Paid";
        case "pending":
          return "Pending";
        case "awaiting_payment":
          return "Awaiting payment";
        case "canceled":
          return "Canceled";
        default:
          return status;
      }
    };

    $scope.statusClass = (status) => {
      switch (status) {
        case "pending":
          return "label-warning";
        case "awaiting_payment":
          return "label-info";
        case "paid":
          return "label-success";
        case "canceled":
          return "label-default";
        default:
          return "label-default";
      }
    };

    this.$onInit = async () => {
      await loadData();
    };
  },
};
